import type { EBoutiqueServicesFragment, ServiceItem } from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import clsx from 'clsx';
import styles from './EBoutiqueServices.module.scss';

export interface EBoutiqueServicesProps {
  /** Contentful EBoutiqueServices data */
  data: EBoutiqueServicesFragment;
  /** Optional className */
  className?: string;
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
}

/**
 * Service icon component that renders the appropriate icon based on type
 */
function ServiceIcon({ icon, className }: { icon?: string | null; className?: string }) {
  switch (icon) {
    case 'shipping':
    case 'truck':
      return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M20 8H4v12h16V8zM20 14h6l4 4v4h-4M20 20H4M8 24a2 2 0 100-4 2 2 0 000 4zM24 24a2 2 0 100-4 2 2 0 000 4z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case 'returns':
    case 'refresh':
      return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M4 16a12 12 0 0121.5-7.3M28 16a12 12 0 01-21.5 7.3M4 4v8h8M28 28v-8h-8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case 'gift':
    case 'gifting':
      return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M26 14v14H6V14M4 10h24v4H4v-4zM16 28V10M16 10c0-2.2-1.8-4-4-4s-4 1.8-4 4M16 10c0-2.2 1.8-4 4-4s4 1.8 4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case 'advisor':
    case 'person':
    case 'support':
      return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M16 14a4 4 0 100-8 4 4 0 000 8zM8 26v-2a4 4 0 014-4h8a4 4 0 014 4v2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    default:
      // Generic service icon
      return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M16 12v4l3 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
}

/**
 * Individual service item
 */
function Service({
  service,
  testIdPrefix,
  index,
}: {
  service: ServiceItem;
  testIdPrefix?: string;
  index: number;
}) {
  const itemTestId = testIdPrefix ? `${testIdPrefix}-item-${index}` : undefined;
  return (
    <div className={styles.service} data-testid={itemTestId}>
      <ServiceIcon icon={service.icon} className={styles.icon} />
      <div className={styles.text}>
        {service.title && (
          <h3 className={styles.title} data-testid={itemTestId ? `${itemTestId}-title` : undefined}>
            {service.title}
          </h3>
        )}
        {service.description && (
          <p
            className={styles.description}
            data-testid={itemTestId ? `${itemTestId}-body` : undefined}
          >
            {service.description}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * E-Boutique services banner
 * Shows service benefits like shipping, returns, gifting, and support
 *
 * Accepts Contentful EBoutiqueServices fragment data
 * Supports both servicesCollection (linked entries) and services (JSON array) formats
 */
export function EBoutiqueServices({ data, className, cmsIndex }: EBoutiqueServicesProps) {
  // Support both collection format and JSON array format
  const collectionServices = filterCollection(data.servicesCollection);
  const services =
    collectionServices.length > 0
      ? collectionServices
      : (data.services as ServiceItem[] | undefined) || [];

  // Generate testid prefix for E2E tests
  const testIdPrefix = cmsIndex !== undefined ? `eboutiqueservices-${cmsIndex}` : undefined;

  if (services.length === 0) {
    return null;
  }

  return (
    <section className={clsx(styles.section, className)} data-testid={testIdPrefix}>
      <div className={styles.container}>
        {data.heading && (
          <h2
            className={styles.heading}
            data-testid={testIdPrefix ? `${testIdPrefix}-title` : undefined}
          >
            {data.heading}
          </h2>
        )}
        <div className={styles.grid}>
          {services.map((service, index) => (
            <Service
              key={service.sys?.id || index}
              service={service}
              testIdPrefix={testIdPrefix}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

EBoutiqueServices.displayName = 'EBoutiqueServices';
